// Modern, clean color design tokens – inspired by SpaceKit template
// Light grey backgrounds, black text, orange accents, subtle shadows
// Reference: https://spacekit-template.webflow.io/

export const tokensDark = {
  grey: {
    0: "#ffffff",
    10: "#fafafa",
    50: "#f5f5f5",
    100: "#eeeeee",
    200: "#e0e0e0",
    300: "#bdbdbd",
    400: "#9e9e9e",
    500: "#757575",
    600: "#616161",
    700: "#424242",
    800: "#303030",
    900: "#212121",
    1000: "#121212",
  },
  primary: {
    // Neutral blacks/greys for text and structure
    100: "#f5f5f5",
    200: "#e0e0e0",
    300: "#bdbdbd",
    400: "#9e9e9e",
    500: "#757575",
    600: "#616161",
    700: "#424242",
    800: "#303030",
    900: "#212121",
  },
  secondary: {
    // Orange accent (SpaceKit style)
    50: "#fff8f0",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#fb923c",
    500: "#f97316",
    600: "#ea580c",
    700: "#c2410c",
    800: "#9a3412",
    900: "#7c2d12",
  },
};

// function that reverses the color palette for dark mode
function reverseTokens(tokensDark) {
  const reversedTokens = {};
  Object.entries(tokensDark).forEach(([key, val]) => {
    const keys = Object.keys(val);
    const values = Object.values(val);
    const length = keys.length;
    const reversedObj = {};
    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = values[length - i - 1];
    }
    reversedTokens[key] = reversedObj;
  });
  return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// MUI theme settings
export const themeSettings = (mode) => {
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // Dark mode palette - bright text on dark backgrounds
            primary: {
              ...tokensDark.primary,
              main: tokensDark.grey[100],
              light: tokensDark.grey[50],
              dark: tokensDark.grey[300],
              100: "#ffffff",
              200: "#f5f5f5",
              300: "#e0e0e0",
              400: "#bdbdbd",
              500: "#9e9e9e",
              600: "#757575",
              700: "#616161",
              800: "#424242",
              900: "#303030",
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[400],
              light: tokensDark.secondary[300],
              dark: tokensDark.secondary[600],
              // Dark mode text colors: 100=white data, 200=orange headers, 300=muted text
              50: "rgba(249, 115, 22, 0.1)",
              100: "#ffffff",        // White - for data text
              200: "#fb923c",        // Orange - for table headers
              300: "#bdbdbd",        // Light grey - for subtitles
              400: "#fb923c",        // Orange accent
              500: "#f97316",        // Orange main
              600: "#424242",        // Dark grey
              700: "#303030",        // Darker grey
              800: "#242424",        // Very dark
              900: "#1a1a1a",        // Near black
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[400],
            },
            background: {
              default: "#1a1a1a",
              alt: "#242424",
              paper: "#2a2a2a",
            },
            text: {
              primary: "#ffffff",
              secondary: "#bdbdbd",
            },
          }
        : {
            // Light mode palette (SpaceKit inspired)
            primary: {
              ...tokensLight.primary,
              main: tokensDark.grey[900],
              light: tokensDark.grey[100],    // Light grey for light backgrounds
              dark: tokensDark.grey[200],     // Slightly darker for table headers
              100: tokensDark.grey[50],
              200: tokensDark.grey[100],
              300: tokensDark.grey[200],
              400: tokensDark.grey[300],
              500: tokensDark.grey[400],
              600: tokensDark.grey[500],
              700: tokensDark.grey[600],
              800: tokensDark.grey[700],
              900: tokensDark.grey[800],
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[500],
              light: tokensDark.secondary[400],
              dark: tokensDark.secondary[700],
              // Light mode text colors: 100=dark text, 200=orange headers, 300=muted text
              50: "rgba(249, 115, 22, 0.08)",
              100: "#212121",        // Dark grey - for data text on light bg
              200: "#f97316",        // Orange - for table headers
              300: "#757575",        // Medium grey - for subtitles
              400: "#fb923c",        // Orange accent
              500: "#f97316",        // Orange main
              600: "#ea580c",        // Darker orange
              700: "#c2410c",        // Even darker orange
              800: "#9a3412",        // Dark brown-orange
              900: "#7c2d12",        // Darkest brown
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.grey[50],  // Light grey background
              alt: tokensDark.grey[0],       // White for cards
              paper: tokensDark.grey[0],     // White paper
            },
            text: {
              primary: tokensDark.grey[900],   // Near black text
              secondary: tokensDark.grey[600], // Muted grey text
            },
          }),
    },
    typography: {
      fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
      fontSize: 14,
      h1: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 600,
      },
      h2: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 600,
      },
      h3: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 600,
      },
      h4: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 600,
      },
      h5: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 600,
      },
      h6: {
        fontFamily: ["Inter", "system-ui", "-apple-system", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 600,
      },
      body1: {
        fontSize: 14,
        lineHeight: 1.6,
      },
      body2: {
        fontSize: 13,
        lineHeight: 1.6,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      "none",
      "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 1px 2px rgba(0, 0, 0, 0.06)",
      "0px 2px 4px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.08)",
      "0px 4px 6px rgba(0, 0, 0, 0.04), 0px 2px 4px rgba(0, 0, 0, 0.06)",
      "0px 6px 8px rgba(0, 0, 0, 0.04), 0px 3px 6px rgba(0, 0, 0, 0.06)",
      "0px 8px 16px rgba(0, 0, 0, 0.06), 0px 4px 8px rgba(0, 0, 0, 0.04)",
      "0px 12px 24px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.04)",
      "0px 16px 32px rgba(0, 0, 0, 0.08), 0px 8px 16px rgba(0, 0, 0, 0.04)",
      "0px 20px 40px rgba(0, 0, 0, 0.08), 0px 10px 20px rgba(0, 0, 0, 0.04)",
      "0px 24px 48px rgba(0, 0, 0, 0.08), 0px 12px 24px rgba(0, 0, 0, 0.04)",
      "0px 28px 56px rgba(0, 0, 0, 0.08), 0px 14px 28px rgba(0, 0, 0, 0.04)",
      "0px 32px 64px rgba(0, 0, 0, 0.08), 0px 16px 32px rgba(0, 0, 0, 0.04)",
      "0px 36px 72px rgba(0, 0, 0, 0.08), 0px 18px 36px rgba(0, 0, 0, 0.04)",
      "0px 40px 80px rgba(0, 0, 0, 0.08), 0px 20px 40px rgba(0, 0, 0, 0.04)",
      "0px 44px 88px rgba(0, 0, 0, 0.08), 0px 22px 44px rgba(0, 0, 0, 0.04)",
      "0px 48px 96px rgba(0, 0, 0, 0.08), 0px 24px 48px rgba(0, 0, 0, 0.04)",
      "0px 52px 104px rgba(0, 0, 0, 0.08), 0px 26px 52px rgba(0, 0, 0, 0.04)",
      "0px 56px 112px rgba(0, 0, 0, 0.08), 0px 28px 56px rgba(0, 0, 0, 0.04)",
      "0px 60px 120px rgba(0, 0, 0, 0.08), 0px 30px 60px rgba(0, 0, 0, 0.04)",
      "0px 64px 128px rgba(0, 0, 0, 0.08), 0px 32px 64px rgba(0, 0, 0, 0.04)",
      "0px 68px 136px rgba(0, 0, 0, 0.08), 0px 34px 68px rgba(0, 0, 0, 0.04)",
      "0px 72px 144px rgba(0, 0, 0, 0.08), 0px 36px 72px rgba(0, 0, 0, 0.04)",
      "0px 76px 152px rgba(0, 0, 0, 0.08), 0px 38px 76px rgba(0, 0, 0, 0.04)",
      "0px 80px 160px rgba(0, 0, 0, 0.08), 0px 40px 80px rgba(0, 0, 0, 0.04)",
      "0px 84px 168px rgba(0, 0, 0, 0.08), 0px 42px 84px rgba(0, 0, 0, 0.04)",
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            fontWeight: 500,
            padding: "8px 20px",
          },
          contained: {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.08)",
            "&:hover": {
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.06)",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.mode === "dark" 
              ? "rgba(255, 255, 255, 0.05)" 
              : tokensDark.grey[100],
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontWeight: 600,
            color: theme.palette.mode === "dark" 
              ? "#fb923c" 
              : "#f97316",  // Orange for light mode headers too
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          notchedOutline: {
            borderRadius: 8,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 8,
            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
            marginTop: 4,
            // Prevent dropdown option panels from sprawling too wide.
            // Long options wrap (see MuiMenuItem) instead of stretching the menu.
            maxWidth: "min(92vw, 340px)",
            minWidth: "unset !important",
            backgroundColor: theme.palette.mode === "dark" 
              ? theme.palette.background.paper 
              : "#ffffff",
            border: theme.palette.mode === "dark" 
              ? "1px solid rgba(255,255,255,0.1)" 
              : "1px solid rgba(0,0,0,0.08)",
          }),
          list: {
            padding: "4px 0",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            fontSize: "0.875rem",
            padding: "6px 12px",
            minHeight: 36,
            lineHeight: 1.4,
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" 
                ? "rgba(255,255,255,0.08)" 
                : "#f5f5f5",
            },
            "&.Mui-selected": {
              backgroundColor: theme.palette.mode === "dark" 
                ? "rgba(249, 115, 22, 0.15)" 
                : "rgba(249, 115, 22, 0.08)",
              "&:hover": {
                backgroundColor: theme.palette.mode === "dark" 
                  ? "rgba(249, 115, 22, 0.25)" 
                  : "rgba(249, 115, 22, 0.12)",
              },
            },
          }),
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.15)",
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
            },
          },
        },
      },
    },
  };
};
